/**
 * EmployeePhotoBlock.jsx
 * ════════════════════════════════════════════════════════════════
 * Блок фото + ФІО + базові дані для шапки картки працівника.
 * Вставляється ПЕРЕД секцією "Дані особи" в EmployeeModal.
 *
 * Використання в index.jsx:
 *   import EmployeePhotoBlock from './EmployeePhotoBlock';
 *
 *   // В modal-body, перед секцією "Дані особи":
 *   {employeeData && (
 *     <EmployeePhotoBlock
 *       employee={employee}
 *       employeeId={employee.id}
 *       kindergartenId={kindergartenId}
 *       isEditing={isEditing}
 *       onAvatarChange={(fileId) => setEmployee(prev => ({
 *         ...prev,
 *         publicProfile: { ...prev.publicProfile, avatarFileId: fileId }
 *       }))}
 *     />
 *   )}
 *
 * Props:
 *   employee       {object}   — поточний стан employee зі стейту модалки
 *   employeeId     {string}   — id співробітника (null якщо новий)
 *   kindergartenId {string}
 *   isEditing      {boolean}
 *   onAvatarChange {function} — (fileId) => void, після завантаження нового фото
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPencil, faUpload, faExpand, faUser,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { employeeAPI } from '../actions';
import { constr_uri } from '../../../../../../services/apiClient';
import PhotoViewer from '../../../../../../components/PhotoViewer/PhotoViewer';

const MAX_PHOTOS = 5;
const PHOTO_SIZE_MB = 5;

// ── Основний компонент ────────────────────────────────────────────────────────

const EmployeePhotoBlock = function({
    employee,
    employeeId,
    kindergartenId,
    isEditing,
    onAvatarChange,
}) {
    const [uploading, setUploading]         = useState(false);
    const [photos, setPhotos]               = useState(employee.photos || []);
    const [viewerSrc, setViewerSrc]         = useState(null);
    const [showAllPhotos, setShowAllPhotos] = useState(false);
    const fileInputRef                      = useRef(null);

    // Синхронізуємо photos коли батько завантажить їх через getPhotos
    useEffect(function() {
        if (employee.photos && employee.photos.length > 0) {
            setPhotos(employee.photos);
        }
    }, [employee.photos]);

    // Аватар — або вибране фото, або перше в списку
    var avatarFileId = (employee.publicProfile && employee.publicProfile.avatarFileId)
        || (photos.length > 0 ? null : null); // явно null якщо немає
    var avatar = avatarFileId
        ? photos.find(function(p) { return p.id === avatarFileId; }) || photos[0] || null
        : photos[0] || null;

    var docId = employee.photoDocId || null;
    var avatarSrc = avatar && docId
        ? constr_uri('docfile?doc_id=' + docId + '&file_id=' + avatar.id)
        : null;

    // ── Завантаження фото ─────────────────────────────────────────────────────

    var handleFileSelect = async function(e) {
        var file = e.target.files[0];
        e.target.value = '';
        if (!file) return;

        if (!file.type.match(/image\//i)) {
            toast.error('Оберіть файл зображення (JPG, PNG, WebP)');
            return;
        }
        if (file.size > PHOTO_SIZE_MB * 1024 * 1024) {
            toast.error('Розмір фото не повинен перевищувати ' + PHOTO_SIZE_MB + ' МБ');
            return;
        }
        if (photos.length >= MAX_PHOTOS) {
            toast.error('Максимум ' + MAX_PHOTOS + ' фото на одного працівника');
            return;
        }
        if (!employeeId) {
            toast.error('Спочатку збережіть картку працівника');
            return;
        }

        setUploading(true);
        try {
            var result = await employeeAPI.uploadPhoto(kindergartenId, employeeId, file);
            var newPhoto = { id: result.fileId, name: file.name, mtype: file.type };
            var updatedPhotos = photos.concat([newPhoto]);
            setPhotos(updatedPhotos);

            // Якщо це перше фото — автоматично робимо аватаром
            if (!avatarFileId && updatedPhotos.length === 1) {
                await employeeAPI.setAvatar(kindergartenId, employeeId, result.fileId);
                onAvatarChange && onAvatarChange(result.fileId);
            }

            toast.success('Фото завантажено');
        } catch (err) {
            toast.error('Помилка завантаження фото');
        } finally {
            setUploading(false);
        }
    };

    // ── Видалення фото ────────────────────────────────────────────────────────

    var handleDelete = async function(photoId) {
        if (!window.confirm('Видалити фото?')) return;
        try {
            await employeeAPI.deletePhoto(kindergartenId, employeeId, photoId);
            var updatedPhotos = photos.filter(function(p) { return p.id !== photoId; });
            setPhotos(updatedPhotos);

            // Якщо видалили аватар — беремо наступне
            if (photoId === avatarFileId) {
                var nextId = updatedPhotos.length > 0 ? updatedPhotos[0].id : null;
                if (nextId) {
                    await employeeAPI.setAvatar(kindergartenId, employeeId, nextId);
                }
                onAvatarChange && onAvatarChange(nextId);
            }
        } catch (err) {
            toast.error('Помилка видалення фото');
        }
    };

    // ── Встановити аватар ─────────────────────────────────────────────────────

    var handleSetAvatar = async function(photoId) {
        try {
            await employeeAPI.setAvatar(kindergartenId, employeeId, photoId);
            onAvatarChange && onAvatarChange(photoId);
            toast.success('Аватар встановлено');
        } catch (err) {
            toast.error('Помилка встановлення аватара');
        }
    };

    // ── Відкрити перегляд ─────────────────────────────────────────────────────

    var handleView = function(photo) {
        if (!docId) return;
        var src = constr_uri('docfile?doc_id=' + docId + '&file_id=' + photo.id);
        setViewerSrc({ src: src, name: photo.name, mtype: photo.mtype });
    };

    // ── Основна посада ────────────────────────────────────────────────────────

    var mainPosition = employee.departments && employee.departments.length > 0
        ? (employee.publicProfile && employee.publicProfile.publicPosition)
            || employee.departments[0].positionName
        : '';

    var formatDate = function(d) {
        if (!d) return '—';
        return new Date(d).toLocaleDateString('uk-UA');
    };

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <>
            {/* ── Шапка: фото + ФІО ── */}
            <div style={{
                display: 'flex', gap: 20, marginBottom: 20,
                paddingBottom: 20, borderBottom: '1px solid #e5e7eb',
            }}>
                {/* Фото-блок */}
                <div style={{ flexShrink: 0, position: 'relative' }}>
                    {/* Аватар */}
                    <div
                        onClick={function() { avatar && handleView(avatar); }}
                        style={{
                            width: 130, height: 170,
                            borderRadius: 10,
                            border: '1px solid #e5e7eb',
                            background: '#f3f4f6',
                            overflow: 'hidden',
                            cursor: avatar ? 'pointer' : 'default',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            position: 'relative',
                        }}
                    >
                        {avatarSrc ? (
                            <img
                                src={avatarSrc}
                                alt={employee.fullName}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ) : (
                            <FontAwesomeIcon icon={faUser}
                                style={{ fontSize: 40, color: '#d1d5db' }} />
                        )}

                        {/* Overlay expand при наявності фото */}
                        {avatar && (
                            <div style={{
                                position: 'absolute', inset: 0,
                                background: 'rgba(0,0,0,0)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'background 0.15s',
                            }}
                                onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(0,0,0,0.25)'; }}
                                onMouseLeave={function(e) { e.currentTarget.style.background = 'rgba(0,0,0,0)'; }}
                            >
                                <FontAwesomeIcon icon={faExpand}
                                    style={{ fontSize: 18, color: '#fff', opacity: 0, transition: 'opacity 0.15s' }}
                                    onMouseEnter={function(e) { e.currentTarget.style.opacity = '1'; }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Кнопка олівця — редагувати фото */}
                    <button
                        type="button"
                        onClick={function() {
                            if (isEditing) {
                                setShowAllPhotos(function(v) { return !v; });
                            } else if (avatar) {
                                handleView(avatar);
                            }
                        }}
                        title={isEditing ? 'Керувати фото' : 'Переглянути фото'}
                        style={{
                            position: 'absolute', bottom: 6, right: 6,
                            width: 26, height: 26, borderRadius: 6,
                            background: isEditing ? '#0069ff' : 'rgba(0,0,0,0.5)',
                            border: 'none', cursor: 'pointer',
                            color: '#fff', fontSize: 11,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <FontAwesomeIcon icon={isEditing ? faPencil : faExpand} />
                    </button>
                </div>

                {/* ФІО + базові дані */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 3 }}>
                        {employee.fullName || [employee.lastName, employee.firstName, employee.middleName].filter(Boolean).join(' ') || '—'}
                    </div>
                    {mainPosition && (
                        <div style={{ fontSize: 14, color: '#0069ff', fontWeight: 600, marginBottom: 14 }}>
                            {mainPosition}
                        </div>
                    )}
                    <div style={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr',
                        gap: '8px 16px',
                    }}>
                        {[
                            { label: 'РНОКПП', value: employee.rnokpp || '—' },
                            { label: 'Дата народження', value: formatDate(employee.birthDate) },
                            { label: 'Стать', value: employee.gender || '—' },
                            { label: 'Освіта', value: employee.education || '—' },
                        ].map(function(row) {
                            return (
                                <div key={row.label}>
                                    <div style={{ fontSize: 12, color: '#6b7280' }}>{row.label}</div>
                                    <div style={{ fontSize: 14, color: '#111827', fontWeight: 500 }}>{row.value}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ── Панель управління фото (тільки в режимі редагування) ── */}
            {isEditing && showAllPhotos && (
                <div style={{
                    marginBottom: 20, padding: 14,
                    border: '1px solid #e5e7eb', borderRadius: 8,
                    background: '#f9fafb',
                }}>
                    <div style={{
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', marginBottom: 12,
                    }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                            Фото ({photos.length} / {MAX_PHOTOS})
                        </div>
                        {photos.length < MAX_PHOTOS && (
                            <button
                                type="button"
                                onClick={function() { fileInputRef.current && fileInputRef.current.click(); }}
                                disabled={uploading}
                                style={{
                                    padding: '5px 12px', borderRadius: 6, fontSize: 12,
                                    border: '1px solid #0069ff', background: '#fff',
                                    color: '#0069ff', cursor: 'pointer', fontWeight: 600,
                                    display: 'flex', alignItems: 'center', gap: 5,
                                }}
                            >
                                {uploading
                                    ? <span className="spinner-border spinner-border-sm" />
                                    : <><FontAwesomeIcon icon={faUpload} />Додати фото</>}
                            </button>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                        />
                    </div>

                    {photos.length === 0 ? (
                        <div
                            onClick={function() { fileInputRef.current && fileInputRef.current.click(); }}
                            style={{
                                border: '1.5px dashed #d1d5db', borderRadius: 6,
                                padding: '20px', textAlign: 'center',
                                cursor: 'pointer', color: '#9ca3af', fontSize: 13,
                            }}
                        >
                            <FontAwesomeIcon icon={faUpload} style={{ fontSize: 20, marginBottom: 6, display: 'block', margin: '0 auto 6px' }} />
                            Натисніть щоб додати фото
                        </div>
                    ) : (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {photos.map(function(photo) {
                                var isAvatar = photo.id === (employee.publicProfile && employee.publicProfile.avatarFileId)
                                    || (!employee.publicProfile?.avatarFileId && photos[0].id === photo.id);
                                var src = docId
                                    ? constr_uri('docfile?doc_id=' + docId + '&file_id=' + photo.id)
                                    : null;

                                return (
                                    <div key={photo.id} style={{ position: 'relative' }}>
                                        <div
                                            onClick={function() { handleView(photo); }}
                                            style={{
                                                width: 72, height: 96, borderRadius: 6,
                                                overflow: 'hidden', cursor: 'pointer',
                                                border: isAvatar ? '2px solid #0069ff' : '2px solid #e5e7eb',
                                                background: '#f3f4f6',
                                            }}
                                        >
                                            {src && (
                                                <img src={src} alt=""
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            )}
                                        </div>

                                        {/* Аватар бейдж */}
                                        {isAvatar && (
                                            <div style={{
                                                position: 'absolute', top: 3, left: 3,
                                                background: '#0069ff', borderRadius: 3,
                                                padding: '1px 4px', fontSize: 9, color: '#fff', fontWeight: 700,
                                            }}>
                                                аватар
                                            </div>
                                        )}

                                        {/* Дії */}
                                        <div style={{
                                            position: 'absolute', bottom: 3, right: 3,
                                            display: 'flex', gap: 2,
                                        }}>
                                            {!isAvatar && (
                                                <button type="button"
                                                    onClick={function() { handleSetAvatar(photo.id); }}
                                                    title="Зробити аватаром"
                                                    style={{
                                                        width: 20, height: 20, borderRadius: 3, fontSize: 9,
                                                        background: 'rgba(0,105,255,0.85)', border: 'none',
                                                        cursor: 'pointer', color: '#fff',
                                                    }}
                                                >A</button>
                                            )}
                                            <button type="button"
                                                onClick={function() { handleDelete(photo.id); }}
                                                title="Видалити"
                                                style={{
                                                    width: 20, height: 20, borderRadius: 3, fontSize: 9,
                                                    background: 'rgba(220,53,69,0.85)', border: 'none',
                                                    cursor: 'pointer', color: '#fff',
                                                }}
                                            >✕</button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
                        Максимум {MAX_PHOTOS} фото · до {PHOTO_SIZE_MB} МБ кожне · JPG, PNG, WebP
                    </div>
                </div>
            )}

            {/* Повноекранний перегляд */}
            {viewerSrc && (
                <PhotoViewer
                    src={viewerSrc.src}
                    name={viewerSrc.name}
                    mtype={viewerSrc.mtype}
                    onClose={function() { setViewerSrc(null); }}
                />
            )}
        </>
    );
};

export default EmployeePhotoBlock;